import { FALLBACK_CATEGORY, normalizeStandardCategoryKey } from "../constants";
import { type GroceryItem } from "../types/grocery";
import { isRetryableSharedListListenerError } from "../utils/sharedListListenerError";
import { type SharedListMutationStore } from "./sharedListMutationStore";

export type ShoppingChange =
  | { kind: "add"; name: string; operationId: string }
  | { kind: "rename"; itemId: string; name: string; operationId: string }
  | { kind: "check"; itemId: string; isChecked: boolean; checkedAtMs?: number }
  | { kind: "remove"; itemId: string };

export interface MutationReceipt {
  itemId?: string;
  category?: string;
  categoryDisplayName?: string;
}

export interface SharedListAuthority {
  allowEditing?: boolean;
  expiresAt?: number;
  accessUnavailable?: boolean;
  authorityReady?: boolean;
}

interface MutationRecord {
  [key: string]: unknown;
  version: 1;
  id: string;
  listId: string;
  timestamp: number;
  sequence?: number;
  retryCount: number;
  maxRetries: number;
  change: ShoppingChange;
  status: "pending" | "confirmed" | "failed";
  settledAt?: number;
  failure?: string;
  receipt?: MutationReceipt;
}

interface PendingMutation {
  record: MutationRecord;
  durable: boolean;
  visible: boolean;
  retryAt: number;
  snapshotRevision: number;
  confirmedRevision?: number;
  settledDurable?: boolean;
  resolve?: () => void;
  reject?: (error: Error) => void;
}

export interface SharedListMutationState {
  items: GroceryItem[];
  queuedOperationsCount: number;
  isSyncing: boolean;
  mutationError: string | null;
}

interface Dependencies {
  store: SharedListMutationStore;
  execute: (listId: string, change: ShoppingChange) => Promise<MutationReceipt>;
  now?: () => number;
  newId?: () => string;
  delivery?: SharedListMutationDelivery;
}

const text = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;
const number = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);
const object = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;
const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : "Shared Grocery List change failed.";

// Version 1 accepts the previous queue's capability-bound records. Unknown
// versions and ambiguous identities are never repaired by guessing a route.
function decodeRecord(value: unknown): MutationRecord | undefined {
  if (
    !object(value) ||
    !text(value.id) ||
    !text(value.listId) ||
    !number(value.timestamp) ||
    !Number.isInteger(value.retryCount) ||
    Number(value.retryCount) < 0 ||
    !Number.isInteger(value.maxRetries) ||
    Number(value.maxRetries) < 1 ||
    Number(value.maxRetries) > 3
  )
    return;
  if (
    value.sequence !== undefined &&
    (!number(value.sequence) ||
      !Number.isSafeInteger(value.sequence) ||
      value.sequence < 0)
  )
    return;
  let change: unknown = value.change;
  if (value.version === undefined) {
    if (!object(value.data) || value.data.sharedListId !== value.listId) return;
    const data = value.data;
    if (value.type === "ADD_ITEM") {
      change = { kind: "add", name: data.name, operationId: data.operationId };
    } else if (value.type === "DELETE_ITEM") {
      change = { kind: "remove", itemId: value.itemId };
    } else if (value.type === "UPDATE_ITEM" && typeof data.name === "string") {
      change = {
        kind: "rename",
        itemId: value.itemId,
        name: data.name,
        operationId: data.operationId,
      };
    } else if (value.type === "UPDATE_ITEM" || value.type === "TOGGLE_ITEM") {
      change = {
        kind: "check",
        itemId: value.itemId,
        isChecked: data.isChecked,
        checkedAtMs: data.checkedAtMs,
      };
    } else return;
  } else if (value.version !== 1) return;
  if (!object(change)) return;
  switch (change.kind) {
    case "add":
      if (!text(change.name) || !text(change.operationId)) return;
      break;
    case "rename":
      if (
        !text(change.itemId) ||
        !text(change.name) ||
        !text(change.operationId)
      )
        return;
      break;
    case "check":
      if (
        !text(change.itemId) ||
        typeof change.isChecked !== "boolean" ||
        (change.checkedAtMs !== undefined && !number(change.checkedAtMs))
      )
        return;
      break;
    case "remove":
      if (!text(change.itemId)) return;
      break;
    default:
      return;
  }
  const status = value.version === undefined ? "pending" : value.status;
  if (status !== "pending" && status !== "confirmed" && status !== "failed")
    return;
  if (status !== "pending" && !number(value.settledAt)) return;
  if (status === "failed" && !text(value.failure)) return;
  if (
    value.receipt !== undefined &&
    (!object(value.receipt) ||
      (value.receipt.itemId !== undefined && !text(value.receipt.itemId)) ||
      (value.receipt.category !== undefined && !text(value.receipt.category)) ||
      (value.receipt.categoryDisplayName !== undefined &&
        !text(value.receipt.categoryDisplayName)))
  )
    return;
  if (
    status === "confirmed" &&
    change.kind === "add" &&
    (!object(value.receipt) || !text(value.receipt.itemId))
  )
    return;
  return {
    version: 1,
    id: value.id,
    listId: value.listId,
    timestamp: value.timestamp,
    sequence: number(value.sequence) ? value.sequence : undefined,
    retryCount: Number(value.retryCount),
    maxRetries: Number(value.maxRetries),
    change: change as ShoppingChange,
    status,
    settledAt: value.settledAt as number | undefined,
    failure: value.failure as string | undefined,
    receipt: value.receipt as MutationReceipt | undefined,
  };
}

/** Browser-lifetime delivery ownership. A route may stop observing a request,
 * but a newer route must wait for its actual completion before changing that
 * same Grocery Item. Idle capabilities are not retained here.
 */
export class SharedListMutationDelivery {
  private deliveries = new Map<string, Promise<void>>();

  async run(
    listId: string,
    itemId: string,
    deliver: () => Promise<void>,
  ): Promise<void> {
    const key = JSON.stringify([listId, itemId]);
    const previous = this.deliveries.get(key) ?? Promise.resolve();
    const current = previous.catch(() => undefined).then(deliver);
    this.deliveries.set(key, current);
    try {
      await current;
    } finally {
      if (this.deliveries.get(key) === current) this.deliveries.delete(key);
    }
  }
}

/** Owns the Shared Grocery List mutation lifecycle behind the route's hook.
 * Snapshots remain authoritative; ordered contributions are removed individually.
 * Promise fulfillment means backend confirmation, never merely local durability.
 */
export class SharedListMutations {
  private authoritative: GroceryItem[];
  private snapshotRevision = 0;
  private mutations: PendingMutation[] = [];
  private running = new Set<string>();
  private listeners = new Set<() => void>();
  private online: boolean;
  private disposed = false;
  private revoked = false;
  private nextSequence = 0;
  private authority: SharedListAuthority;
  private timer?: ReturnType<typeof setTimeout>;
  private expiryTimer?: ReturnType<typeof setTimeout>;
  private initialized: Promise<void>;
  private initializationError?: unknown;
  private mutationError: string | null = null;
  private state: SharedListMutationState;
  private now: () => number;
  private newId: () => string;
  private delivery: SharedListMutationDelivery;
  private pruning = false;
  private pruneRequested = false;

  constructor(
    readonly listId: string,
    private dependencies: Dependencies,
    initialItems: GroceryItem[] = [],
    online = true,
    authority: SharedListAuthority = {},
  ) {
    this.authoritative = initialItems;
    this.online = online;
    this.authority = authority;
    this.revoked = authority.accessUnavailable === true;
    this.now = dependencies.now ?? Date.now;
    this.newId = dependencies.newId ?? (() => crypto.randomUUID());
    this.delivery = dependencies.delivery ?? new SharedListMutationDelivery();
    this.state = {
      items: initialItems,
      queuedOperationsCount: 0,
      isSyncing: false,
      mutationError: null,
    };
    this.initialized = this.restore();
    this.scheduleExpiry();
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };
  getSnapshot = (): SharedListMutationState => this.state;

  dispose(): void {
    this.disposed = true;
    clearTimeout(this.timer);
    clearTimeout(this.expiryTimer);
    this.listeners.clear();
    // In-flight delivery still records its receipt. Other durable work waits for
    // the next owning route; it is never redirected or destroyed by unmounting.
  }

  setOnline(online: boolean): void {
    this.online = online;
    this.pump();
  }

  setAuthority(authority: SharedListAuthority): void {
    this.revoked ||= authority.accessUnavailable === true;
    this.authority = { ...authority, accessUnavailable: this.revoked };
    this.scheduleExpiry();
    this.publish();
    this.pump();
  }

  replaceSnapshot(items: GroceryItem[]): void {
    if (this.disposed || this.authority.accessUnavailable) return;
    this.authoritative = items;
    this.snapshotRevision += 1;
    for (const mutation of this.mutations) {
      if (
        mutation.record.status === "confirmed" &&
        (this.reflected(mutation.record) ||
          (mutation.confirmedRevision !== undefined &&
            this.snapshotRevision > mutation.confirmedRevision))
      )
        mutation.visible = false;
    }
    this.publish();
    void this.pruneSettled();
  }

  addItem = (name: string, _selectedCategory = ""): Promise<void> => {
    if (!name.trim()) return this.rejectInput();
    return this.enqueue({
      kind: "add",
      name: name.trim(),
      operationId: this.newId(),
    });
  };
  toggleItem = (
    itemId: string,
    currentStatus: boolean,
    checkedAt?: Date,
  ): Promise<void> =>
    this.enqueue({
      kind: "check",
      itemId,
      isChecked: !currentStatus,
      ...(!currentStatus
        ? { checkedAtMs: checkedAt?.getTime() ?? this.now() }
        : {}),
    });
  deleteItem = (itemId: string): Promise<void> =>
    this.enqueue({ kind: "remove", itemId });
  updateItem = (
    itemId: string,
    updates: { name?: string; category?: string },
  ): Promise<void> => {
    if (!updates.name?.trim() || updates.category !== undefined)
      return this.rejectInput();
    return this.enqueue({
      kind: "rename",
      itemId,
      name: updates.name.trim(),
      operationId: this.newId(),
    });
  };

  private rejectInput(): Promise<void> {
    const error = new Error("Shared Grocery List change is unavailable.");
    this.mutationError = error.message;
    this.publish();
    return Promise.reject(error);
  }

  private accessError(): Error | undefined {
    if (
      !this.listId ||
      this.authority.accessUnavailable ||
      this.authority.allowEditing === false ||
      (this.authority.expiresAt !== undefined &&
        this.authority.expiresAt <= this.now())
    ) {
      return new Error("Shared Grocery List editing is unavailable.");
    }
  }

  private scheduleExpiry(): void {
    clearTimeout(this.expiryTimer);
    const expiresAt = this.authority.expiresAt;
    if (expiresAt === undefined || this.disposed || expiresAt <= this.now())
      return;
    this.expiryTimer = setTimeout(
      () => {
        this.publish();
        this.pump();
        this.scheduleExpiry();
      },
      Math.min(expiresAt - this.now(), 2_147_483_647),
    );
  }

  private enqueue(change: ShoppingChange): Promise<void> {
    if (
      this.disposed ||
      this.authority.authorityReady === false ||
      this.accessError()
    )
      return this.rejectInput();
    if (
      change.kind !== "add" &&
      (!text(change.itemId) ||
        !this.state.items.some((item) => item.id === change.itemId))
    )
      return this.rejectInput();
    const record: MutationRecord = {
      version: 1,
      id: this.newId(),
      listId: this.listId,
      timestamp: this.now(),
      retryCount: 0,
      maxRetries: 3,
      change,
      status: "pending",
    };
    this.mutationError = null;
    const promise = new Promise<void>((resolve, reject) => {
      const mutation: PendingMutation = {
        record,
        durable: false,
        visible: true,
        retryAt: 0,
        snapshotRevision: this.snapshotRevision,
        resolve,
        reject,
      };
      this.mutations.push(mutation);
      this.publish();
      void this.persistNew(mutation);
    });
    return promise;
  }

  private async persistNew(mutation: PendingMutation): Promise<void> {
    try {
      await this.initialized;
      if (this.initializationError)
        throw new Error(errorMessage(this.initializationError));
      mutation.record.sequence = this.nextSequence = Math.max(
        this.now() * 1_000,
        this.nextSequence + 1,
      );
      await this.dependencies.store.put(mutation.record);
      mutation.durable = true;
      this.publish();
      this.pump();
    } catch (error) {
      mutation.record.status = "failed";
      mutation.visible = false;
      this.mutationError = errorMessage(error);
      this.publish();
      mutation.reject?.(new Error(this.mutationError));
      mutation.resolve = mutation.reject = undefined;
      this.pump();
    }
  }

  private async restore(): Promise<void> {
    try {
      const stored = await this.dependencies.store.load();
      const records = stored
        .map(decodeRecord)
        .filter((record): record is MutationRecord => record !== undefined);
      const ownRecords = records
        .filter((record) => record.listId === this.listId)
        .sort(
          (a, b) =>
            (a.sequence ?? a.timestamp * 1_000) -
            (b.sequence ?? b.timestamp * 1_000),
        );
      this.nextSequence = Math.max(
        0,
        ...ownRecords.map(
          (record) => record.sequence ?? record.timestamp * 1_000,
        ),
      );
      // Restored receipts resolve identity dependencies but are not historical
      // route actions. In particular they cannot retrospectively enable a CTA.
      const restored = ownRecords.map((record) => ({
        record,
        durable: true,
        visible: record.status === "pending",
        settledDurable: record.status !== "pending",
        retryAt: 0,
        snapshotRevision: this.snapshotRevision,
      }));
      this.mutations = [...restored, ...this.mutations];
      const failure = ownRecords
        .filter((record) => record.status === "failed")
        .at(-1);
      if (failure && (failure.settledAt ?? 0) >= this.now() - 86_400_000)
        this.mutationError = failure.failure ?? null;
      this.publish();
      await this.pruneSettled();
      this.pump();
    } catch (error) {
      this.initializationError = error;
      // A failed storage initialization does not prevent reading the list.
      // The first attempted mutation reports the inability to retain its work.
    }
  }

  private canonicalId(itemId: string): string | undefined {
    if (!itemId.startsWith("optimistic-")) return itemId;
    const parent = this.mutations.find(
      (mutation) => `optimistic-${mutation.record.id}` === itemId,
    );
    return parent?.record.status === "confirmed"
      ? parent.record.receipt?.itemId
      : undefined;
  }

  private itemKey(record: MutationRecord): string {
    return record.change.kind === "add"
      ? `optimistic-${record.id}`
      : (this.canonicalId(record.change.itemId) ?? record.change.itemId);
  }

  private pump(): void {
    if (this.disposed || this.authority.authorityReady === false) return;
    clearTimeout(this.timer);
    let nextRetry = Infinity;
    const blocked = new Set<string>();
    for (const mutation of this.mutations) {
      const { record } = mutation;
      if (record.status !== "pending") continue;
      const key = this.itemKey(record);
      if (blocked.has(key)) continue;
      blocked.add(key);
      if (!mutation.durable || this.running.has(record.id)) continue;
      const denied = this.accessError();
      if (denied) {
        this.running.add(record.id);
        void this.settle(mutation, undefined, denied).finally(() => {
          this.running.delete(record.id);
          this.publish();
          this.pump();
        });
        continue;
      }
      if (!this.online) continue;
      if (
        record.change.kind !== "add" &&
        !this.canonicalId(record.change.itemId)
      ) {
        const pendingItemId = record.change.itemId;
        const parent = this.mutations.find(
          (candidate) => `optimistic-${candidate.record.id}` === pendingItemId,
        );
        if (!parent || parent.record.status === "failed") {
          this.running.add(record.id);
          void this.settle(
            mutation,
            undefined,
            new Error("The pending Grocery Item could not be added."),
          ).finally(() => {
            this.running.delete(record.id);
            this.publish();
            this.pump();
          });
        }
        continue;
      }
      if (mutation.retryAt > this.now()) {
        nextRetry = Math.min(nextRetry, mutation.retryAt);
        continue;
      }
      this.running.add(record.id);
      void this.deliver(mutation);
    }
    if (Number.isFinite(nextRetry))
      this.timer = setTimeout(() => this.pump(), nextRetry - this.now());
    this.publish();
  }

  private async deliver(mutation: PendingMutation): Promise<void> {
    try {
      await this.delivery.run(this.listId, this.itemKey(mutation.record), () =>
        this.deliverOwned(mutation),
      );
    } finally {
      this.running.delete(mutation.record.id);
      this.publish();
      void this.pruneSettled();
      this.pump();
    }
  }

  private async deliverOwned(mutation: PendingMutation): Promise<void> {
    let { record } = mutation;
    try {
      // Restoration can race the previous route's receipt transaction. Read
      // again while holding delivery ownership instead of replaying stale work.
      const latest = (await this.dependencies.store.load())
        .map(decodeRecord)
        .find(
          (stored) => stored?.id === record.id && stored.listId === this.listId,
        );
      if (latest) {
        mutation.record = record = latest;
        if (latest.status !== "pending") {
          mutation.settledDurable = true;
          mutation.visible =
            latest.status === "confirmed" && !this.reflected(latest);
          mutation.confirmedRevision = this.snapshotRevision;
          if (latest.failure) this.mutationError = latest.failure;
          this.complete(mutation);
          return;
        }
      }
      if (this.disposed || !this.online) return;
      if (record.retryCount >= record.maxRetries) {
        await this.settle(
          mutation,
          undefined,
          new Error(
            "Shared Grocery List change failed after repeated delivery attempts.",
          ),
        );
        return;
      }
      record.retryCount += 1;
      await this.dependencies.store.put(record);
      // Permission may have changed while waiting for the durable transaction.
      const denied = this.accessError();
      if (denied) {
        await this.settle(mutation, undefined, denied);
        return;
      }
      if (this.disposed || !this.online) {
        record.retryCount -= 1;
        await this.dependencies.store.put(record);
        return;
      }
      const change =
        record.change.kind === "add"
          ? record.change
          : {
              ...record.change,
              itemId: this.canonicalId(record.change.itemId)!,
            };
      const receipt = await this.dependencies.execute(record.listId, change);
      if (record.change.kind === "add" && !text(receipt.itemId))
        throw new Error("The Grocery Item confirmation is invalid.");
      await this.settle(mutation, receipt);
    } catch (error) {
      if (
        isRetryableSharedListListenerError(error) &&
        record.retryCount < record.maxRetries
      ) {
        mutation.retryAt = this.now() + 1_000 * 2 ** (record.retryCount - 1);
      } else await this.settle(mutation, undefined, error);
    }
  }

  private async settle(
    mutation: PendingMutation,
    receipt?: MutationReceipt,
    failure?: unknown,
  ): Promise<void> {
    const { record } = mutation;
    record.status = failure === undefined ? "confirmed" : "failed";
    record.settledAt = this.now();
    record.receipt = receipt;
    record.failure = failure === undefined ? undefined : errorMessage(failure);
    if (failure === undefined) {
      // A later confirmed intent supersedes earlier confirmed contributions on
      // that field, even when the earlier echo never reached this listener.
      for (const previous of this.mutations) {
        if (previous === mutation) break;
        if (
          previous.record.status === "confirmed" &&
          this.itemKey(previous.record) === this.itemKey(record) &&
          (previous.record.change.kind === record.change.kind ||
            record.change.kind === "remove")
        )
          previous.visible = false;
      }
    }
    mutation.confirmedRevision =
      failure === undefined ? this.snapshotRevision : undefined;
    mutation.visible = failure === undefined && !this.reflected(record);
    if (record.failure) this.mutationError = record.failure;
    try {
      await this.dependencies.store.put(record);
      mutation.settledDurable = true;
    } catch (storageError) {
      // Keep the previously committed pending record until a terminal receipt
      // commits. Retry settlement independently; never redeliver known outcomes
      // or delete the only durable evidence when its replacement write fails.
      this.mutationError = errorMessage(storageError);
      this.retrySettlement(mutation, 1);
    }
    this.complete(mutation);
    void this.pruneSettled();
  }

  private complete(mutation: PendingMutation): void {
    this.publish();
    if (mutation.record.status === "confirmed") mutation.resolve?.();
    else mutation.reject?.(new Error(mutation.record.failure));
    mutation.resolve = mutation.reject = undefined;
  }

  private retrySettlement(mutation: PendingMutation, attempt: number): void {
    if (attempt >= 3) return;
    setTimeout(
      () => {
        void this.delivery
          .run(this.listId, this.itemKey(mutation.record), async () => {
            // A later owner may already have committed this receipt. Do not
            // overwrite its outcome or resurrect a record it has since pruned.
            const stored = (await this.dependencies.store.load())
              .map(decodeRecord)
              .find((record) => record?.id === mutation.record.id);
            if (stored?.status === "pending")
              await this.dependencies.store.put(mutation.record);
            mutation.settledDurable = true;
          })
          .then(() => this.pruneSettled())
          .catch((error) => {
            this.mutationError = errorMessage(error);
            this.publish();
            this.retrySettlement(mutation, attempt + 1);
          });
      },
      1_000 * 2 ** (attempt - 1),
    );
  }

  private needsAddPresentation(record: MutationRecord): boolean {
    if (
      record.status !== "confirmed" ||
      record.change.kind !== "add" ||
      this.authoritative.some((item) => item.id === record.receipt?.itemId)
    )
      return false;
    return this.mutations.some(
      ({ record: dependent, visible }) =>
        (dependent.status === "pending" || visible) &&
        dependent.change.kind !== "add" &&
        (dependent.change.itemId === `optimistic-${record.id}` ||
          dependent.change.itemId === record.receipt?.itemId),
    );
  }

  private async pruneSettled(): Promise<void> {
    // Only prune this route's known committed outcomes. A disposed route can
    // still finish a receipt write, but must not delete a new owner's records.
    if (this.disposed) return;
    if (this.pruning) {
      this.pruneRequested = true;
      return;
    }
    this.pruning = true;
    try {
      const protectedIds = new Set(
        this.mutations
          .filter(
            ({ record, visible }) =>
              (record.status === "pending" || visible) &&
              record.change.kind !== "add",
          )
          .map(({ record }) =>
            record.change.kind !== "add" ? record.change.itemId : "",
          ),
      );
      const settled = this.mutations
        .filter(({ record }) => record.status !== "pending")
        .sort(
          (a, b) =>
            (b.record.settledAt ?? 0) - (a.record.settledAt ?? 0) ||
            (b.record.sequence ?? b.record.timestamp * 1_000) -
              (a.record.sequence ?? a.record.timestamp * 1_000),
        );
      for (const [index, mutation] of settled.entries()) {
        const { record } = mutation;
        if (index < 100 && (record.settledAt ?? 0) >= this.now() - 86_400_000)
          continue;
        // Unreflected contributions are active presentation, not history. Their
        // receipt remains usable if a shopper edits the displayed item.
        if (
          mutation.visible ||
          !mutation.settledDurable ||
          this.running.has(record.id) ||
          protectedIds.has(`optimistic-${record.id}`) ||
          this.needsAddPresentation(record)
        )
          continue;
        await this.dependencies.store.remove(record.id);
        this.mutations = this.mutations.filter((entry) => entry !== mutation);
      }
    } catch {
      // Retention is best effort; a cleanup failure never changes the outcome.
    } finally {
      this.pruning = false;
      if (this.pruneRequested) {
        this.pruneRequested = false;
        void this.pruneSettled();
      }
    }
  }

  private reflected(record: MutationRecord): boolean {
    const mutation = this.mutations.find(
      (candidate) => candidate.record === record,
    );
    if (mutation && this.snapshotRevision <= mutation.snapshotRevision)
      return false;
    const { change, receipt } = record;
    const id =
      change.kind === "add" ? receipt?.itemId : this.canonicalId(change.itemId);
    const item = this.authoritative.find((candidate) => candidate.id === id);
    if (change.kind === "remove") return !item;
    if (!item) return false;
    if (change.kind === "check")
      return (
        item.isChecked === change.isChecked &&
        (!change.isChecked ||
          change.checkedAtMs === undefined ||
          item.checkedAt?.getTime() === change.checkedAtMs)
      );
    return (
      item.name === change.name &&
      (!receipt?.category ||
        (normalizeStandardCategoryKey(item.category) || item.category) ===
          (normalizeStandardCategoryKey(receipt.category) || receipt.category))
    );
  }

  private publish(): void {
    if (this.disposed) return;
    let items = [...this.authoritative];
    for (const { record, visible } of this.mutations) {
      if (
        (!visible && !this.needsAddPresentation(record)) ||
        record.status === "failed"
      )
        continue;
      const { change, receipt } = record;
      const id =
        change.kind === "add"
          ? (receipt?.itemId ?? `optimistic-${record.id}`)
          : (this.canonicalId(change.itemId) ?? change.itemId);
      const category = receipt?.category
        ? normalizeStandardCategoryKey(receipt.category) || receipt.category
        : undefined;
      if (change.kind === "add") {
        // A snapshot can precede its callable receipt. Once canonical identity is
        // known, preserve that snapshot and layer only subsequent local intents.
        if (!items.some((item) => item.id === id))
          items.unshift({
            id,
            name: change.name,
            category: category ?? FALLBACK_CATEGORY,
            isChecked: false,
            createdAt: new Date(record.timestamp),
            categoryDisplayName: receipt?.categoryDisplayName,
            categorizationStatus: category
              ? category === FALLBACK_CATEGORY
                ? "fallback"
                : "resolved"
              : "pending",
          });
      } else if (change.kind === "remove")
        items = items.filter((item) => item.id !== id);
      else
        items = items.map((item) =>
          item.id !== id
            ? item
            : change.kind === "check"
              ? {
                  ...item,
                  isChecked: change.isChecked,
                  checkedAt:
                    change.isChecked && change.checkedAtMs !== undefined
                      ? new Date(change.checkedAtMs)
                      : undefined,
                }
              : {
                  ...item,
                  name: change.name,
                  ...(category
                    ? {
                        category,
                        categoryDisplayName: receipt?.categoryDisplayName,
                      }
                    : {}),
                  categorizationStatus: category
                    ? category === FALLBACK_CATEGORY
                      ? "fallback"
                      : "resolved"
                    : "pending",
                },
        );
    }
    if (
      this.authority.accessUnavailable ||
      (this.authority.expiresAt !== undefined &&
        this.authority.expiresAt <= this.now())
    )
      items = [];
    this.state = {
      items,
      queuedOperationsCount: this.mutations.filter(
        (mutation) => mutation.durable && mutation.record.status === "pending",
      ).length,
      isSyncing: this.running.size > 0,
      mutationError: this.mutationError,
    };
    this.listeners.forEach((listener) => listener());
  }
}
